
import { useStore } from '@/store'
import * as THREE from 'three'
import { computed, watch, defineComponent, inject, onUnmounted } from 'vue'
import References from '@/singletons/references'
import BagAnimationService from '@/services/Three/bag-animation.service'
import { ThreeActionTypes } from '@/store/modules/three/action-types'
import SentrySingleton from '@/singletons/sentry-singleton'

export default defineComponent({
  name: 'ThreeRandselAnimation',
  setup() {
    const store = useStore()
    const emitter: any = inject('emitter')
    const configLoaded = computed(() => store.getters.configLoaded)
    const isBagLoaded = computed(() => store.getters.isBagLoaded)
    let raycaster: THREE.Raycaster
    let mouse: THREE.Vector2
    let lastClickTime: number
    let bagAnimationService: BagAnimationService

    // prettier-ignore
    watch(() => configLoaded.value, (_) => {
      startAnimation()
    })

    // prettier-ignore
    watch(() => isBagLoaded.value, (_) => {
      // happens when changing from 2d to 3d
      if (isBagLoaded.value && configLoaded.value) {
        startAnimation()
      }
    })

    function startAnimation() {
      // console.log('startAnimation')
      try {
        bagAnimationService = new BagAnimationService()
        bagAnimationService.startTweens(store.getters.isAllConfigSet, onAnimationFinish)
        emitter.emit('startAnimation')
        setupClicks()
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function onAnimationFinish() {
      // console.log('animation finished')
      store.dispatch(ThreeActionTypes.SET_ANIMATION_FINISHED, true)
      store.dispatch(ThreeActionTypes.SET_CONTROLS_ENABLED, true)
    }

    emitter.on('animateBag', (obj: any) => {
      if (bagAnimationService) {
        bagAnimationService.tweenCamera(obj.pos, obj.instant)
        bagAnimationService.setBagOpen(obj.open, obj.instant)
      }
    })

    emitter.on('update', () => {
      if (bagAnimationService) {
        bagAnimationService.update()
      }
    })

    function setupClicks() {
      raycaster = new THREE.Raycaster()
      mouse = new THREE.Vector2()
      window.addEventListener('dblclick', onDocumentClick, false)
      window.addEventListener('mousedown', onDocumentMouseDown, false)
      window.addEventListener('touchstart', onDocumentTouchStart, false)
    }

    function onDocumentClick(event) {
      try {
        if (lastClickTime + 0.3 * 1000 < new Date().getTime() || event.target !== document.querySelector('#scene-container canvas')) {
          return
        }
        mouse.x = (event.clientX / window.innerWidth) * 2 - 1
        mouse.y = -(event.clientY / window.innerHeight) * 2 + 1
        raycaster.setFromCamera(mouse, References.instance.getSceneReferences().camera)
        var intersects = raycaster.intersectObjects(References.instance.getSceneReferences().scene.children, true)
        if (intersects.length > 0) {
          bagAnimationService.toggleAnimation()
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    function onDocumentMouseDown(event: any) {
      lastClickTime = new Date().getTime()
    }

    function onDocumentTouchStart(event) {
      try {
        if (event.touches.length != 1) {
          return
        }
        var now = new Date().getTime()
        var timesince = now - lastClickTime
        if (timesince < 600 && timesince > 0) {
          event.clientX = event.touches[0].pageX
          event.clientY = event.touches[0].pageY
          onDocumentClick(event)
        }
        lastClickTime = new Date().getTime()
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    }

    onUnmounted(() => {
      try {
        if (bagAnimationService) {
          bagAnimationService.cancelAll()
        }
      } catch (err) {
        SentrySingleton.instance.captureException(err)
      }
    })

    return {}
  }
})
